import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SelectedMember } from "@components/common/member-autocomplete/member-autocomplete-selection";
import { useChangeProjectManagementBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-change-project-management-helper";
import { BaseCompanyIdProps, GroupId } from "@custom-types/base-types";
import { SdbProject } from "@custom-types/project-types";
import { GroupAndProjectManagerSelection } from "@pages/project-details/project-overview/change-management/group-and-project-manager-selection";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { isObject } from "es-toolkit/compat";
import { useEffect, useState } from "react";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, ManagementIcon } from "@flat-ui";

/** Button allowing to change management of project in company */
export function BulkChangeProjectManagement({
  companyId,
}: BaseCompanyIdProps): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [payload, setPayload] =
    useState<FaroCoreApiTypes.UpdateProjectDetailsPayloadProps>({});
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId>();
  const [selectedProjectManager, setSelectedProjectManager] =
    useState<SelectedMember>(null);

  const bulkActionName = useAppSelector(bulkActionNameSelector);

  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));

  const { bulkDialogContent, successDialogContent } =
    useChangeProjectManagementBulkDialogContent();
  const mode = useBulkActionsMode();

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  // Set the payload when the selected group or project manager changes
  useEffect(() => {
    if (!companyId) {
      throw new Error("Company id is required!");
    }

    if (
      selectedProjectManager === null ||
      typeof selectedGroupId === "undefined"
    ) {
      setPayload({});
    } else {
      setPayload({
        projectManagerIdentity: isObject(selectedProjectManager)
          ? selectedProjectManager.identity
          : selectedProjectManager,
        groupId: String(selectedGroupId),
      });
    }
  }, [companyId, selectedGroupId, selectedProjectManager]);

  function onNewGroupClicked(): void {
    dispatch(setBulkActionName("changeProjectManagement"));

    setShouldShowDialog(true);
  }

  const config = getBulkActionButtonConfig(mode, "Project management");

  return (
    <>
      <FaroTextButton
        onClick={onNewGroupClicked}
        tooltipText={config.tooltipText}
        loadingTrackColor={config.loadingTrackColor}
        startIcon={<ManagementIcon sx={{ width: "18px", height: "18px" }} />}
        rippleColor={config.rippleColor}
        sx={config.sx}
      >
        {config.shouldShowText && <Text variant="buttonM">Management</Text>}
      </FaroTextButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeProjectManagement" &&
        shouldShowSuccessDialog && (
          <BulkSuccessDialog dialogContent={successDialogContent} />
        )}

      {bulkActionName === "changeProjectManagement" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeGroup}
          bulkActionCallback={(project: SdbProject) =>
            dispatch(
              updateProjectDetails({
                projectId: project.id,
                payload,
              })
            ).unwrap()
          }
          shouldDisableConfirmButton={Object.keys(payload).length === 0}
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="projects"
        >
          <GroupAndProjectManagerSelection
            selectedGroupId={selectedGroupId}
            setSelectedGroupId={setSelectedGroupId}
            setSelectedProjectManager={setSelectedProjectManager}
            selectedProjectManager={selectedProjectManager}
            companyId={companyId}
          />
        </FaroBulkDialog>
      )}
    </>
  );
}
